import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiCookie, FiCheck } = FiIcons

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false)
  const bannerRef = useRef(null)

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookieConsent = localStorage.getItem('cookieConsent')
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  // Focus management and trap
  useEffect(() => {
    if (isVisible && bannerRef.current) {
      // Set initial focus to the banner container or first button
      const focusableElements = bannerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusableElements.length > 0) {
        focusableElements[0].focus()
      }

      const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          const focusableElements = bannerRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
          const firstElement = focusableElements[0]
          const lastElement = focusableElements[focusableElements.length - 1]

          if (e.shiftKey) { // Shift + Tab
            if (document.activeElement === firstElement) {
              e.preventDefault()
              lastElement.focus()
            }
          } else { // Tab
            if (document.activeElement === lastElement) {
              e.preventDefault()
              firstElement.focus()
            }
          }
        }
      }

      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isVisible])

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted')
    localStorage.setItem('cookieConsentDate', new Date().toISOString())
    setIsVisible(false)
    // Move focus back to main content after dismissal
    document.getElementById('main-content')?.focus()
  }

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined')
    localStorage.setItem('cookieConsentDate', new Date().toISOString())
    setIsVisible(false)
    // Move focus back to main content after dismissal
    document.getElementById('main-content')?.focus()
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={bannerRef}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-[100] bg-white border-t border-gray-200 shadow-2xl"
          role="region"
          aria-label="Cookie Consent"
          aria-describedby="cookie-banner-description"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {/* Cookie Icon & Content */}
              <div className="flex items-start space-x-3 flex-1">
                <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiCookie} className="text-orange-700 text-lg" />
                </div>
                <div className="flex-1">
                  <h2 id="cookie-banner-title" className="text-sm font-black uppercase tracking-widest text-gray-900 mb-1">
                    Cookie Consent
                  </h2>
                  <p id="cookie-banner-description" className="text-xs text-gray-700 leading-relaxed font-medium">
                    We use essential cookies to make our site work and analytics cookies to understand how you use our site. 
                    <Link 
                      to="/cookie-policy" 
                      className="text-orange-700 hover:text-orange-900 underline ml-1 font-bold"
                    >
                      Learn more
                    </Link>
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3 w-full sm:w-auto">
                <button
                  onClick={handleDecline}
                  className="flex-1 sm:flex-none px-6 py-2.5 text-xs font-black uppercase tracking-widest text-gray-600 hover:text-gray-900 border border-gray-300 rounded-none hover:bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                >
                  Decline
                </button>
                <button
                  onClick={handleAccept}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-6 py-2.5 bg-orange-700 hover:bg-orange-800 text-white text-xs font-black uppercase tracking-widest rounded-none transition-all duration-200 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                >
                  <SafeIcon icon={FiCheck} className="mr-2 text-sm" />
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default CookieBanner